// Surface builder for the SRID ladder (A2 producer wiring). Groups a batch's features by their
// distinct SRID, reprojects geographically-resolvable ones to WGS84, and applies the mixed-outcome
// rule: geographic features render; unresolvable ones are DROPPED and counted in the projection note;
// the WHOLE map goes planar (raw features + a `computePlanarTransform` affine) ONLY when no feature
// resolves geographically. The surface (T11 peek; later T13/T14/T16) hands the result to `SpatialMap`.

import type { SpatialGeoJson } from '../decode/types';
import type { PlanarTransform, SpatialMapFeature } from '../map/spatialMapTypes';
import { computeFitBounds } from '../map/spatialMapView';
import { computePlanarTransform } from '../map/planarTransform';
import type { SridResolution } from './sridLadder';

export type ProjectedFeatures = {
  /** Features ready for `SpatialMap`: reprojected to WGS84 (geographic) or raw (whole-map planar). */
  features: SpatialMapFeature[];
  /** Set only when the whole map is planar (no feature resolved geographically). */
  planarTransform: PlanarTransform | null;
  /** Note chip text: dropped-unresolved-feature summary (mixed) or the planar reason (whole-planar). */
  projectionNote: string | null;
};

function reproject(feature: SpatialMapFeature, toWgs84: (g: SpatialGeoJson) => SpatialGeoJson): SpatialMapFeature {
  return {
    ...feature,
    geometry: { ...feature.geometry, geojson: toWgs84(feature.geometry.geojson), srid: 4326 },
  };
}

/** "3 features in unrecognized SRID 999 not shown · 1 feature in unrecognized SRID 4 not shown". */
function droppedNote(counts: Map<number | null, number>): string {
  return [...counts.entries()]
    .map(([srid, n]) => `${n} feature${n === 1 ? '' : 's'} in unrecognized SRID ${srid ?? 'none'} not shown`)
    .join(' · ');
}

export function projectFeaturesForMap(
  features: SpatialMapFeature[],
  resolutions: Map<number | null, SridResolution>,
): ProjectedFeatures {
  const geographic: SpatialMapFeature[] = [];
  const dropped = new Map<number | null, number>();

  for (const feature of features) {
    const resolution = resolutions.get(feature.geometry.srid);
    if (resolution && resolution.mode === 'geographic') {
      geographic.push(reproject(feature, resolution.toWgs84));
    } else {
      const srid = feature.geometry.srid;
      dropped.set(srid, (dropped.get(srid) ?? 0) + 1);
    }
  }

  // At least one feature resolved geographically → render those, drop + report the rest.
  if (geographic.length > 0) {
    return {
      features: geographic,
      planarTransform: null,
      projectionNote: dropped.size > 0 ? droppedNote(dropped) : null,
    };
  }

  // No feature resolved geographically → whole map planar: keep raw features, fit an affine to them.
  const bbox = computeFitBounds(features);
  const firstNote = [...resolutions.values()].find((r) => r.mode === 'planar');
  return {
    features,
    planarTransform: bbox ? computePlanarTransform(bbox) : null,
    projectionNote:
      firstNote && firstNote.mode === 'planar'
        ? firstNote.note
        : 'Local or unknown coordinate system — coordinates shown in raw data space.',
  };
}
